import React, { useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components Styled
import {
  Container,
  DropDownSelectStyled,
  NavContainer,
  SubContainer,
} from './styles';

// Components
import { Accordion } from '../../../../components/navigation/Accordion';
import { BreadCrumbs } from '../../../../components/navigation/Breadcrumbs';

// Icons
import HomeIcon from '../../../../assets/icons/home.svg';

// Routes utils
import { getOptions } from '../../../../router/definitionGloblaRoutes';
import { createRoutes } from '../../../../router/labelToRoutes';
import { filterAllowedRoutes } from '../../../../router/utils';
import { getData } from './routes';
import { OptionSelected } from '../../../../components/inputs/DropDown/DropDownList/types';
import { Role } from '../../../../types/auth';

// Custom hooks
import { useIsSmallDesktop } from '../../../../hooks/useMediaBreakPoints';
import { NavigationFallback } from '../../../../components/navigation/NavigationFallback';

// Redux
import { roidOTSelectedReset } from '../../../../state/actions/ui/roidOTSelectedGeneralView';
import { useDispatch } from 'react-redux';

// Auth
import { useAuth } from '../../../../hooks/useAuth';

// Permisos nativos
import { useNotificationsPermissionPrompt } from '../../../../hooks/useNotificationsPermissionPrompt';

export default function SurveillanceRouteLayout(): JSX.Element {
  const { t } = useTranslation('pages');
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useAuth();

  /**
   * Permiso de notificaciones, al ENTRAR en el módulo de recorridos.
   *
   * Aquí y no en el arranque de la app porque Android manda pedir los permisos
   * EN CONTEXTO — «ask for a permission in context, when the user starts to
   * interact with the feature that requires it» — y para las notificaciones
   * insiste: «before you ask users to grant any permissions, let them
   * familiarize themselves with your app». Un diálogo suelto nada más entrar,
   * sin nada a la vista que lo justifique, se deniega más; y una negativa es
   * PARA SIEMPRE, porque Android no vuelve a enseñar el diálogo.
   *
   * Y aquí y no en `start()` —que era lo de antes— porque este layout envuelve
   * TODO el módulo: se monta al abrir el listado, o sea con el operario aún en
   * la oficina y no ya en el coche a punto de salir. Mismo contexto, varios
   * minutos antes.
   *
   * Solo rol Tablet: es quien captura. En web es un no-op.
   */
  useNotificationsPermissionPrompt(
    (user?.user?.rolActive as Role | undefined) === Role.Tablet,
  );
  const currentRole = user?.user?.rolActive;
  // Fix 2026-07-20 (QA usuario): este switcher pasaba el array estático
  // `options` SIN filtrar por rol (un Tablet veía Comunicacions/Informes/
  // Gestió/Administració) y sin traducir. Patrón canónico de TODOS los demás
  // layouts (WorkOrders/Budgets/Management/Admin/Reporting):
  // getOptions(t) + filterAllowedRoutes(userRole).
  const optionsFiltered = currentRole
    ? filterAllowedRoutes(getOptions(t), currentRole as Role)
    : getOptions(t);
  const routes = location.pathname.split('/');
  routes?.shift();

  const routesBreadCrumb = createRoutes(routes, [
    {
      content: <img alt={t('dashboard.mainPage')} src={HomeIcon} />,
      path: '/',
      pathAbsolute: '/',
    },
  ]);

  const onClickAccordion = useCallback(() => {
    dispatch(roidOTSelectedReset());
  }, []);

  const isSmallDesktop = useIsSmallDesktop();

  return (
    <Container>
      <BreadCrumbs data={routesBreadCrumb} />
      <SubContainer>
        <NavContainer>
          {isSmallDesktop ? null : (
            <>
              <DropDownSelectStyled
                options={optionsFiltered as OptionSelected[]}
                defaultOption={routes[0]}
              />
              <Accordion
                data={getData(t, currentRole)}
                onItemClick={onClickAccordion}
              />
            </>
          )}
        </NavContainer>
        <NavigationFallback>
          <Outlet />
        </NavigationFallback>
      </SubContainer>
    </Container>
  );
}
